import React, { useCallback, useMemo, useState } from "react";

import Alert from "veneer/Alert";
import Drawer from "veneer/Drawer";
import { showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";

import { APP_ID_SINGLE_ZONE_SCHEDULER } from "cortex/ui/src/constant/application";
import { Application } from "cortex/ui/src/fleet/view/application/model";
import { Device, FormData } from "cortex/ui/src/fleet/view/device/model";
import SystemStrategyForm from "cortex/ui/src/fleet/view/strategy/component/SystemStrategyForm";
import { getStrategyContentAppId } from "cortex/ui/src/fleet/view/strategy/utils";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import {
  applicationsSelector,
  getAllByIdExceptSchedulers,
} from "cortex/ui/src/redux-store/applications/selectors";
import { EDIT_DEVICE } from "cortex/ui/src/redux-store/devices/actions";
import { getDeviceTags } from "cortex/ui/src/redux-store/devices/selectors";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import { NumberOption } from "cortex/ui/src/ui/table/types";

const getAllAppsAsArray = makeGetAllAsArray<Application>();

type StrategyFallbackContents = NonNullable<FormData["fallbackContents"]>;
type StrategyFormValue = {
  fallbackContents: StrategyFallbackContents;
  selectedApplication: FormData["selectedApplication"];
};

type EditPayload = {
  fallbackContents: string;
  id: Device["id"];
  location: Device["location"];
  mainApp: string;
  name: Device["name"];
  networkId: Device["network_id"];
  operateParams: Device["operate_time"];
  tags: string[];
  testing: Device["testing"];
};

interface SystemStrategyDrawerProps {
  onCancel: () => void;
  open: boolean;
  selectedItems: ReadonlyArray<Device>;
  setSelectedItems: (items: ReadonlyArray<Device>) => void;
}

export default function SystemStrategyDrawer({
  onCancel,
  open,
  selectedItems,
  setSelectedItems,
}: SystemStrategyDrawerProps) {
  const dispatch = useAppDispatch();
  const [value, setValue] = useState<StrategyFormValue>({
    fallbackContents: [],
    selectedApplication: null,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const allAppsById = useAppSelector(applicationsSelector);
  const appById = getAllByIdExceptSchedulers({
    byId: allAppsById,
  });
  const allApps = getAllAppsAsArray({ byId: allAppsById });
  const apps = getAllAppsAsArray(appById);
  const installedApps = useMemo(
    () => apps.filter((app) => app.is_installed && !app.hide_on_ui),
    [apps],
  );
  const schedulerId = useMemo(
    () =>
      allApps.find((app) => app.name === APP_ID_SINGLE_ZONE_SCHEDULER)?.id ??
      132,
    [allApps],
  );

  const applicationOptions: ReadonlyArray<NumberOption> = useMemo(
    () =>
      installedApps
        .map((app) => ({
          label: app.display_name,
          value: app.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [installedApps],
  );

  const devicesWithContentSource = selectedItems.filter((device) => {
    return getStrategyContentAppId(device) !== null;
  });

  const handleClose = useCallback(() => {
    setErrorMessage(null);
    onCancel();
  }, [onCancel]);

  const getEditPayload = useCallback(
    (device: Device): EditPayload => ({
      fallbackContents:
        value.fallbackContents.length > 0
          ? JSON.stringify(value.fallbackContents)
          : "",
      id: device.id,
      location: device.location,
      mainApp: value.selectedApplication
        ? JSON.stringify({
            appId: value.selectedApplication.value,
            scheduler: schedulerId,
          })
        : "",
      name: device.name,
      networkId: device.network_id,
      operateParams: device.operate_time,
      tags: getDeviceTags(device)
        .map((t) => t.label)
        .filter((label): label is string => Boolean(label)),
      testing: device.testing,
    }),
    [schedulerId, value],
  );

  const runBulkEdit = useCallback(
    async (payloads: EditPayload[], successMessage: string) => {
      let successCount = 0;
      let failureCount = 0;

      for (const editOpts of payloads) {
        try {
          await createPromiseApiAction(dispatch, EDIT_DEVICE, [
            editOpts.id,
            editOpts.fallbackContents,
            editOpts.mainApp,
            editOpts.name,
            editOpts.networkId,
            editOpts.testing,
            editOpts.location,
            editOpts.tags,
            editOpts.operateParams,
          ]);
          successCount += 1;
        } catch (e) {
          failureCount += 1;
        }
      }

      if (failureCount > 0) {
        setErrorMessage(
          `Failed to update ${failureCount} device(s). Please try again.`,
        );
      }

      if (successCount > 0) {
        showSuccess(successMessage.replace("{count}", String(successCount)));
      }
    },
    [dispatch],
  );

  const handleUpdateDevices = useCallback(async () => {
    setErrorMessage(null);
    if (selectedItems.length === 0) return;

    if (value.fallbackContents.length > 0 && !value.selectedApplication) {
      setErrorMessage(
        "Please select an application when setting fallback creatives.",
      );
      return;
    }

    if (value.selectedApplication && value.fallbackContents.length === 0) {
      setErrorMessage(
        "Please select at least one fallback creative when setting an application.",
      );
      return;
    }
    setSubmitted(true);
    const editPayloads: EditPayload[] = selectedItems.map(getEditPayload);

    try {
      await runBulkEdit(
        editPayloads,
        "Successfully updated {count} device(s).",
      );
    } catch (e) {
      setErrorMessage(`An error occurred while updating devices: ${e}`);
    } finally {
      setSelectedItems([]);
      setSubmitted(false);
      handleClose();
    }
  }, [handleClose, runBulkEdit, selectedItems, setSelectedItems, value]);

  const handleRemoveSystemStrategy = useCallback(async () => {
    setErrorMessage(null);
    if (selectedItems.length === 0) return;
    setSubmitted(true);
    const editPayloads: EditPayload[] = selectedItems.map((device) => ({
      ...getEditPayload(device),
      fallbackContents: "",
      mainApp: "",
    }));

    try {
      await runBulkEdit(
        editPayloads,
        "Successfully removed content source from {count} device(s).",
      );
    } catch (e) {
      setErrorMessage(`An error occurred while updating devices: ${e}`);
    } finally {
      setSelectedItems([]);
      setSubmitted(false);
      handleClose();
    }
  }, [handleClose, runBulkEdit, selectedItems]);

  return (
    <Drawer
      commitLabel="Save"
      commitLoading={submitted}
      onCancel={handleClose}
      onClose={handleClose}
      onCommit={handleUpdateDevices}
      onSecondary={handleRemoveSystemStrategy}
      open={open}
      secondaryLabel="Revert to Network Defaults"
      size="large"
      title="Edit Content Source"
    >
      <Stack spacing={2}>
        {devicesWithContentSource.length > 0 && (
          <Alert dense severity="warning">
            <b>{devicesWithContentSource.length}</b> out of the{" "}
            <b>{selectedItems.length}</b> selected devices already contain
            content sources. Updating the content source will overwrite their
            existing application and fallback creatives.
          </Alert>
        )}
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        <Alert dense severity="info">
          By updating the content source, the selected devices will override
          their network's default application and fallback creatives. This
          change will apply to {selectedItems.length} device(s).
        </Alert>
        <SystemStrategyForm
          applicationOptions={applicationOptions}
          onChange={(nextValue) => {
            setValue({ ...value, ...nextValue });
          }}
          type="device"
          value={value}
        />
      </Stack>
    </Drawer>
  );
}
